import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '../store/structureDefinitions/cartState';

import { cartECouponAdapter } from '../store/adapters/cartECouponAdapters';
import { isUnappliedActiveEcoupon } from '../utils/ecouponUtils';

const cartEcouponsSelectors = cartECouponAdapter.getSelectors(
    (state: RootState) => state.cart.ecoupons,
);

const isCartInitialized = (state: RootState) => state.cart.cartLoading.isInitialized;
const isCartReady = (state: RootState) =>
    state.cart.cartLoading.isInitialized && !state.cart.cartLoading.isLoading;

const isProductDataAvailable = (state: RootState) => state.cart.cartLoading.isProductDataAvailable;
const cartId = (state: RootState) => state.cart.summary.cartId;
const currentCartDeliveryMode = (state: RootState) => state.cart.summary.delivery;
const ecoupons = createSelector(
    (state: RootState) => cartEcouponsSelectors.selectAll(state),
    (rawEcoupons) =>
        rawEcoupons.map((ecoupon) => ({
            ...ecoupon,
            expirationTime: new Date(ecoupon.expirationTime),
        })),
);

const unappliedActiveEcoupons = createSelector(ecoupons, (ecouponsList) =>
    ecouponsList.filter(isUnappliedActiveEcoupon),
);
const appliedEcoupons = createSelector(ecoupons, (ecouponsList) =>
    ecouponsList.filter((ecoupon) => ecoupon.active),
);
const selectedTimeslot = (state: RootState) =>
    (!!state.cart.timeslot && state.cart.timeslot) || null;

const hasSelectedTimeslot = (state: RootState) => !!selectedTimeslot(state);

const isInEditOrder = (state: RootState) => isInCartEditOrder(state.cart);
const editOrderNumber = (state: RootState) => cartEditOrderNumber(state.cart);
const cartEditOrderNumber = (cart: RootState['cart']) => cart.editOrder.editOrderNumber;
const editOrderSummary = (state: RootState) => state.cart.editOrder.editOrderSummary;
const isInCartEditOrder = (state: RootState['cart']) => state.editOrder.isInEditOrderMode;
const paymentExpirationDate = (state: RootState) => state.cart.editOrder.paymentExpirationDate;

const editOrderDate = (state: RootState) =>
    (state.cart.timeslot?.startTime && new Date(state.cart.timeslot.startTime)) || null;

const startTimeEditOrderTimeslotSelector = (cart: RootState['cart']) =>
    cartEditOrderTimeslot(cart)?.startTime;
const endTimeEditOrderTimeslotSelector = (cart: RootState['cart']) =>
    cartEditOrderTimeslot(cart)?.endTime;

const cartEditOrderTimeslotStartTime = createSelector(
    startTimeEditOrderTimeslotSelector,
    (startTime) => {
        return startTime ? new Date(startTime) : null;
    },
);
const postalCode = (state: RootState) => state.cart.summary.postCode;
const cartEditOrderTimeslotEndTime = createSelector(endTimeEditOrderTimeslotSelector, (endTime) => {
    return endTime ? new Date(endTime) : null;
});

const editOrderTimeslot = (state: RootState) => cartEditOrderTimeslot(state.cart);

const cartEditOrderTimeslot = (cart: RootState['cart']) =>
    (isInCartEditOrder(cart) && cart.timeslot) || null;

const editOrderTimeoutSelector = (state: RootState) => state.cart.editOrder?.editOrderTimeout;

const editOrderExpirationTime = createSelector(editOrderTimeoutSelector, (timeout) =>
    timeout ? new Date(timeout) : null,
);

const selectCommentAboutCustomer = (state: RootState) => state.cart.commentAboutCustomer;

export const cartSelectors = {
    selectCommentAboutCustomer,
    selectedTimeslot,
    isCartInitialized,
    isProductDataAvailable,
    hasSelectedTimeslot,
    editOrderDate,
    editOrderExpirationTime,
    isInEditOrder,
    editOrderNumber,
    cartEditOrderNumber,
    editOrderSummary,
    cartId,
    editOrderTimeslot,
    cartEditOrderTimeslotStartTime,
    cartEditOrderTimeslotEndTime,
    isCartReady,
    currentCartDeliveryMode,
    paymentExpirationDate,
    postalCode,
    ecoupons,
    unappliedActiveEcoupons,
    appliedEcoupons,
};
